/**
 * GS1-128 (Code 128) encoder + canvas renderer for IMpb barcodes.
 *
 * SCOPE / DIVISION OF LABOUR — read this before editing:
 *   This file does SYMBOLOGY only (bars and spaces). It knows nothing about USPS field
 *   widths, constructs, MOD 10, or human-readable suppression. All of that lives in
 *   shared/impb/impb.js (71/71 conformance tests) and is vendored byte-identically to
 *   web/vendor/impb.js. Do NOT reimplement barcode DATA logic here.
 *
 * The two check digits are different things and must not be conflated:
 *   - MOD 10  = the DATA check digit, part of the PIC. Computed by impb.js.
 *   - MOD 103 = the Code 128 SYMBOLOGY check digit, not part of the data. Computed here.
 *
 * SELF-VERIFICATION: the pattern table below was transcribed by hand, which is exactly the
 * kind of thing that fails silently. `verifyPatternTable()` proves every entry is
 * structurally valid (11 modules, 6 alternating elements, widths 1..4; Stop = 13/7) and
 * `decodeModules()` lets a test decode a rendered symbol back to digits. A typo that
 * preserves structure would still be caught by the encode -> decode round-trip.
 */

'use strict';

(function (root, factory) {
  const api = factory();
  if (typeof module === 'object' && module && module.exports) module.exports = api;
  if (root) root.Barcode = api;
})(typeof globalThis !== 'undefined' ? globalThis : this, function () {
  /**
   * Code 128 element-width patterns for symbol values 0..106.
   * Each string is bar,space,bar,space,bar,space widths in modules (sums to 11).
   * Value 106 (Stop) is the sole exception: 7 elements, 13 modules.
   */
  const PATTERNS = [
    '212222', '222122', '222221', '121223', '121322', '131222', '122213', '122312',
    '132212', '221213', '221312', '231212', '112232', '122132', '122231', '113222',
    '123122', '123221', '223211', '221132', '221231', '213212', '223112', '312131',
    '311222', '321122', '321221', '312212', '322112', '322211', '212123', '212321',
    '232121', '111323', '131123', '131321', '112313', '132113', '132311', '211313',
    '231113', '231311', '112133', '112331', '132131', '113123', '113321', '133121',
    '313121', '211331', '231131', '213113', '213311', '213131', '311123', '311321',
    '331121', '312113', '312311', '332111', '314111', '221411', '431111', '111224',
    '111422', '121124', '121421', '141122', '141221', '112214', '112412', '122114',
    '122411', '142112', '142211', '241211', '221114', '413111', '241112', '134111',
    '111242', '121142', '121241', '114212', '124112', '124211', '411212', '421112',
    '421211', '212141', '214121', '412121', '111143', '111341', '131141', '114113',
    '114311', '411113', '411311', '113141', '114131', '311141', '411131', '211412',
    '211214', '211232', '2331112',
  ];

  const START_A = 103;
  const START_B = 104;
  const START_C = 105;
  const STOP = 106;
  const FNC1 = 102;
  const CODE_B = 100; // switch to subset B (from C)
  const CODE_C = 99; // switch to subset C (from B)
  const QUIET_ZONE_MODULES = 10; // GS1 general spec minimum

  /** Structural audit of the hand-transcribed table. Throws on any defect. */
  function verifyPatternTable() {
    if (PATTERNS.length !== 107) {
      throw new Error(`Code128 table must have 107 entries, has ${PATTERNS.length}`);
    }
    PATTERNS.forEach((p, value) => {
      const isStop = value === STOP;
      const wantElements = isStop ? 7 : 6;
      const wantModules = isStop ? 13 : 11;
      if (p.length !== wantElements) {
        throw new Error(`Code128 value ${value}: expected ${wantElements} elements, got ${p.length} (${p})`);
      }
      const widths = p.split('').map(Number);
      if (widths.some((w) => !Number.isInteger(w) || w < 1 || w > 4)) {
        throw new Error(`Code128 value ${value}: element widths must be 1..4 (${p})`);
      }
      const sum = widths.reduce((a, b) => a + b, 0);
      if (sum !== wantModules) {
        throw new Error(`Code128 value ${value}: modules must sum to ${wantModules}, got ${sum} (${p})`);
      }
    });
    // Patterns must be unique, or decoding would be ambiguous.
    const seen = new Set(PATTERNS);
    if (seen.size !== PATTERNS.length) {
      throw new Error('Code128 table contains duplicate patterns — decoding would be ambiguous');
    }
    return true;
  }

  /**
   * Encode digit segments as GS1-128 symbol values.
   *
   * @param {string[]} segments digit-only strings; an FNC1 separator is inserted BETWEEN
   *   segments (GS1 requires one after a variable-length AI element when another follows).
   *   A leading FNC1 is always emitted, which is what makes this GS1-128 rather than plain
   *   Code 128.
   * @returns {{values: number[], checkDigit: number}}
   */
  function encodeValues(segments) {
    const segs = (Array.isArray(segments) ? segments : [segments]).map(String).filter((s) => s.length);
    if (!segs.length) throw new Error('encodeValues: no data');
    for (const s of segs) {
      if (!/^[0-9]+$/.test(s)) throw new Error(`encodeValues: segment must be digits only, got ${JSON.stringify(s)}`);
    }

    const values = [START_C, FNC1];
    let subset = 'C';

    segs.forEach((seg, segIndex) => {
      if (segIndex > 0) {
        // Separator FNC1 between AI elements. Re-enter subset C first if we drifted out.
        if (subset !== 'C') {
          values.push(CODE_C);
          subset = 'C';
        }
        values.push(FNC1);
      }
      let i = 0;
      // Subset C packs two digits per symbol. All IMpb constructs happen to yield
      // even-length segments, but handle the odd tail generically rather than assuming.
      while (seg.length - i >= 2) {
        if (subset !== 'C') {
          values.push(CODE_C);
          subset = 'C';
        }
        values.push(Number(seg.slice(i, i + 2)));
        i += 2;
      }
      if (i < seg.length) {
        // One digit left: subset B encodes ASCII, and '0'..'9' are values 16..25.
        values.push(CODE_B);
        subset = 'B';
        values.push(seg.charCodeAt(i) - 32);
      }
    });

    // MOD 103 symbology check digit: start value + sum(position * value), positions from 1.
    let sum = values[0];
    for (let i = 1; i < values.length; i += 1) sum += i * values[i];
    const checkDigit = sum % 103;

    return { values: values.concat([checkDigit, STOP]), checkDigit };
  }

  /**
   * Turn symbol values into a module stream: array of {bar:boolean, width:int}, plus a
   * flat 0/1 module array. Quiet zones are included so the symbol is actually scannable.
   */
  function toModules(values, { quietZone = QUIET_ZONE_MODULES } = {}) {
    const elements = [];
    if (quietZone > 0) elements.push({ bar: false, width: quietZone });
    for (const v of values) {
      const pattern = PATTERNS[v];
      if (pattern === undefined) throw new Error(`toModules: no pattern for symbol value ${v}`);
      pattern.split('').forEach((w, idx) => {
        elements.push({ bar: idx % 2 === 0, width: Number(w) });
      });
    }
    if (quietZone > 0) elements.push({ bar: false, width: quietZone });

    const bits = [];
    for (const el of elements) for (let i = 0; i < el.width; i += 1) bits.push(el.bar ? 1 : 0);
    return { elements, bits, totalModules: bits.length };
  }

  /**
   * Decode a module bit array back to symbol values. Used by the test suite to prove the
   * rendered symbol really carries the data impb.js produced (round-trip verification).
   */
  function decodeModules(bits) {
    // Collapse to run lengths, dropping leading/trailing quiet zone.
    const runs = [];
    let i = 0;
    while (i < bits.length) {
      let j = i;
      while (j < bits.length && bits[j] === bits[i]) j += 1;
      runs.push({ bar: bits[i] === 1, width: j - i });
      i = j;
    }
    if (runs.length && !runs[0].bar) runs.shift();
    if (runs.length && !runs[runs.length - 1].bar) runs.pop();

    const values = [];
    let k = 0;
    while (k < runs.length) {
      const remaining = runs.length - k;
      const take = remaining === 7 ? 7 : 6;
      if (remaining < take) throw new Error(`decodeModules: trailing ${remaining} elements do not form a symbol`);
      const pattern = runs.slice(k, k + take).map((r) => r.width).join('');
      const value = PATTERNS.indexOf(pattern);
      if (value < 0) throw new Error(`decodeModules: unknown pattern ${pattern} at element ${k}`);
      values.push(value);
      k += take;
    }
    return values;
  }

  /**
   * Decode symbol values back to the digit string + FNC1 markers.
   * @returns {{digits: string, segments: string[], startValue: number, checkDigit: number}}
   */
  function decodeValues(values) {
    if (values.length < 4) throw new Error('decodeValues: too few symbols');
    const startValue = values[0];
    if (![START_A, START_B, START_C].includes(startValue)) {
      throw new Error(`decodeValues: first symbol ${startValue} is not a Start code`);
    }
    if (values[values.length - 1] !== STOP) throw new Error('decodeValues: missing Stop symbol');
    const checkDigit = values[values.length - 2];
    const payload = values.slice(1, values.length - 2);

    // Re-verify MOD 103 over what we decoded.
    const forCheck = [startValue].concat(payload);
    let sum = forCheck[0];
    for (let n = 1; n < forCheck.length; n += 1) sum += n * forCheck[n];
    if (sum % 103 !== checkDigit) {
      throw new Error(`decodeValues: MOD 103 mismatch, computed ${sum % 103}, symbol says ${checkDigit}`);
    }

    let subset = startValue === START_C ? 'C' : startValue === START_B ? 'B' : 'A';
    const segments = [''];
    for (const v of payload) {
      if (v === FNC1 && subset === 'C') {
        // Leading FNC1 opens the GS1 element string; later ones separate AI elements.
        if (segments.length === 1 && segments[0] === '') continue;
        segments.push('');
        continue;
      }
      if (subset === 'C') {
        if (v === CODE_B) { subset = 'B'; continue; }
        if (v > 99) throw new Error(`decodeValues: unexpected subset-C symbol ${v}`);
        segments[segments.length - 1] += String(v).padStart(2, '0');
      } else {
        if (v === CODE_C) { subset = 'C'; continue; }
        if (v === FNC1) { segments.push(''); continue; }
        segments[segments.length - 1] += String.fromCharCode(v + 32);
      }
    }
    return { digits: segments.join(''), segments, startValue, checkDigit };
  }

  /**
   * Full encode of an IMpb barcode object (as returned by impb.js buildBarcode()).
   * Uses the ENCODED digit string, which includes the 420+ZIP routing element that is
   * suppressed from the human-readable text — the barcode must carry it even though the
   * printed digits do not show it.
   */
  function encodeImpb(barcode) {
    if (!barcode || typeof barcode.encoded !== 'string') {
      throw new Error('encodeImpb: expected an impb.js buildBarcode() result');
    }
    const hasRouting = Boolean(barcode.fields && barcode.fields.routingAI);
    // Two AI elements when routing is present: (420)ZIP {FNC1} (92)PIC...
    const segments = hasRouting
      ? [barcode.encoded.slice(0, barcode.encoded.length - barcode.pic.length), barcode.pic]
      : [barcode.encoded];
    const { values, checkDigit } = encodeValues(segments);
    const modules = toModules(values);
    return {
      values,
      mod103: checkDigit,
      bits: modules.bits,
      elements: modules.elements,
      totalModules: modules.totalModules,
      segments,
      symbology: 'GS1-128 (Code 128, subset C, FNC1)',
    };
  }

  /**
   * Render a module stream to a <canvas>. Scales the module width to fill the canvas so
   * the symbol stays proportionally correct at any display size.
   *
   * @param {HTMLCanvasElement} canvas
   * @param {number[]} bits 0/1 module array from toModules()
   * @param {object} [opts]
   * @param {string} [opts.text] human-readable line drawn beneath the bars
   */
  function renderToCanvas(canvas, bits, opts = {}) {
    const {
      moduleWidth = null,
      barHeight = 70,
      textHeight = 22,
      background = '#ffffff',
      foreground = '#000000',
      font = '13px ui-monospace, SFMono-Regular, Menlo, monospace',
      text = '',
    } = opts;

    const dpr = (typeof window !== 'undefined' && window.devicePixelRatio) || 1;
    const cssWidth = canvas.clientWidth || canvas.width || bits.length;

    /*
     * CRISP-EDGE REQUIREMENT: bar boundaries are snapped to whole DEVICE pixels.
     *
     * Drawing at a fractional module width antialiases every edge into a grey ramp. On screen
     * that merely looks soft, but it genuinely degrades decodability — a scanner (and the
     * pixel-decode check in web/verify-ui.js) thresholds each column to bar/space, and a
     * half-lit boundary column can be assigned either way, corrupting an element width. So we
     * pick an INTEGER device-pixel module width and lay every element out on that grid.
     */
    const rawModuleDev = moduleWidth ? moduleWidth * dpr : (cssWidth * dpr) / bits.length;
    const moduleDev = Math.max(1, Math.floor(rawModuleDev));
    const deviceWidth = bits.length * moduleDev;
    const barHeightDev = Math.round(barHeight * dpr);
    const textHeightDev = text ? Math.round(textHeight * dpr) : 0;
    const deviceHeight = barHeightDev + textHeightDev;

    canvas.width = deviceWidth;
    canvas.height = deviceHeight;
    // CSS size in logical px keeps the symbol physically the intended size on any display.
    const cssRenderWidth = deviceWidth / dpr;
    const cssRenderHeight = deviceHeight / dpr;
    canvas.style.width = `${cssRenderWidth}px`;
    canvas.style.height = `${cssRenderHeight}px`;

    const ctx = canvas.getContext('2d');
    // Identity transform: we are drawing in device pixels deliberately.
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.fillStyle = background;
    ctx.fillRect(0, 0, deviceWidth, deviceHeight);

    // Draw contiguous runs of bars, on exact device-pixel boundaries.
    ctx.fillStyle = foreground;
    let idx = 0;
    while (idx < bits.length) {
      if (bits[idx] === 1) {
        let end = idx;
        while (end < bits.length && bits[end] === 1) end += 1;
        ctx.fillRect(idx * moduleDev, 0, (end - idx) * moduleDev, barHeightDev);
        idx = end;
      } else {
        idx += 1;
      }
    }

    if (text) {
      ctx.fillStyle = foreground;
      ctx.font = scaleFont(font, dpr);
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(text, deviceWidth / 2, barHeightDev + textHeightDev / 2);
    }

    /*
     * Self-describing render. These make the drawn symbol independently verifiable (and
     * debuggable) without re-deriving the layout: web/verify-ui.js reads the canvas pixels
     * back, samples on this exact grid, and decodes the result.
     */
    if (canvas.dataset) {
      canvas.dataset.modules = String(bits.length);
      canvas.dataset.moduleDevicePx = String(moduleDev);
      canvas.dataset.devicePixelRatio = String(dpr);
    }

    return {
      width: cssRenderWidth,
      height: cssRenderHeight,
      deviceWidth,
      deviceHeight,
      moduleWidth: moduleDev / dpr,
      moduleDevicePx: moduleDev,
      modules: bits.length,
    };
  }

  /**
   * Scale a CSS font shorthand by the device pixel ratio, since renderToCanvas draws in
   * device pixels. Leaves the string alone if no leading numeric size is found.
   */
  function scaleFont(font, dpr) {
    if (dpr === 1) return font;
    return String(font).replace(/(\d*\.?\d+)px/, (_m, size) => `${Math.round(Number(size) * dpr)}px`);
  }

  /** Convenience: impb barcode object -> drawn canvas, with the spec-grouped PIC below. */
  function drawImpb(canvas, barcode, opts = {}) {
    const encoded = encodeImpb(barcode);
    const render = renderToCanvas(canvas, encoded.bits, {
      text: barcode.humanReadable,
      ...opts,
    });
    return { encoded, render };
  }

  return {
    PATTERNS,
    START_A,
    START_B,
    START_C,
    STOP,
    FNC1,
    CODE_B,
    CODE_C,
    QUIET_ZONE_MODULES,
    verifyPatternTable,
    encodeValues,
    toModules,
    decodeModules,
    decodeValues,
    encodeImpb,
    renderToCanvas,
    drawImpb,
  };
});
